import type { DoctorService, DoctorServiceExpanded } from "@hermes/api";
import { useFlag } from "@hermes/flags";
import { useMessage } from "@hermes/intl";
import {
  colors,
  createStaticStyles,
  cssFns,
  useStaticStyles,
} from "@hermes/ui";
import { useState } from "react";

import { useVisibleWordsFromDOM } from "./useVisibleWordsFromDOM";

const styles = {
  container: createStaticStyles.clean([
    cssFns.typo({
      weight: "regular",
      level: "body-1",
      color: colors.principal.gs4,
    }),
  ]),
  services: createStaticStyles({
    common: [
      cssFns.typo({
        weight: "regular",
        level: "body-1",
      }),
      {
        span: {
          textDecoration: "underline",
          "&:hover": {
            cursor: "pointer",
          },
        },
      },
    ],
    mobile: { marginBlockEnd: 6 },
    desktop: { marginBlockEnd: 8 },
  }),
  extra: createStaticStyles({
    mobile: [cssFns.typo({ level: "body-3", weight: "regular" })],
    desktop: [cssFns.typo({ level: "body-2", weight: "regular" })],
  }),
};

export const DoctorProfileInfoDetailsV2 = ({
  science_degree,
  academic_status,
  experience,
  qualification,
  availableServices,
  displaySpecialties,
}: {
  science_degree?: string | null;
  academic_status?: string | null;
  experience?: number | null;
  qualification?: string | null;
  availableServices?: string[];
  displaySpecialties?: boolean;
  branchServices?: DoctorServiceExpanded[] | DoctorService[];
}) => {
  const message = useMessage();
  const flag = useFlag();
  const { staticStyles } = useStaticStyles();

  const [isExpanded, setIsExpanded] = useState(false);

  const { containerRef, visibleWords, isClamped } = useVisibleWordsFromDOM({
    words: availableServices || [],
    lines: 2,
    enable: displaySpecialties && flag({ id: "short-services" }),
  });

  const displayWords =
    displaySpecialties && flag({ id: "short-services" })
      ? isExpanded
        ? availableServices
        : visibleWords
      : availableServices;

  return (
    <div css={styles.container}>
      {availableServices && (
        <div ref={containerRef} css={staticStyles(styles.services)}>
          {displayWords?.join(", ")}
          {displaySpecialties && (
            <>
              {isClamped && !isExpanded && (
                <>
                  ,&nbsp;
                  <span
                    css={{
                      display: "inline-block",
                      lineHeight: "24px",
                    }}
                    onClick={() => setIsExpanded(true)}
                  >
                    {message({ id: "common.btn.show-more" })}
                  </span>
                </>
              )}
              {isExpanded && (
                <>
                  ,&nbsp;
                  <span
                    css={{
                      display: "inline-block",
                      lineHeight: "24px",
                    }}
                    onClick={() => setIsExpanded(false)}
                  >
                    {message({ id: "common.btn.hide" })}
                  </span>
                </>
              )}
            </>
          )}
        </div>
      )}
      <div css={staticStyles(styles.extra)}>
        {[
          Boolean(experience) &&
            message({
              id: "3f451199-ef63-49d7-a91d-b30bd26ca7e9",
              default:
                "Стаж {years} {years, plural, one {год} few {года} other {лет}}",
              values: {
                years: experience,
              },
            }),
          qualification,
          academic_status,
          science_degree,
        ]
          .truthy()
          .join(" / ")}
      </div>
    </div>
  );
};
